/**
 * Route registry for the ported PoseidonFrontend screens.
 *
 * Two things changed when this module moved into PlatformFE, and both are deliberate:
 *
 * 1. **Every value below is relative, with no leading `/`.** `main/initializers/axios.ts`
 *    sets `baseURL` to `config.apiBaseUrl` (`/api/v1/platform`), and axios joins a
 *    relative url onto it. A leading slash is *not* treated as an absolute URL by axios
 *    (only a `scheme://` is), so `/api/user/list` would have resolved to
 *    `/api/v1/platform/api/user/list` and 404'd. Relative values are the only correct
 *    form here.
 *
 * 2. **Platform-owned groups were retargeted to PlatformAPI's v1 surface** (ADR-004: the
 *    Platform owns identity, session, organization, membership, entitlements, feed,
 *    notification and activity). Those groups are marked `PLATFORM` below and name the
 *    route the screen now calls. Groups that belong to a *product* (invoice, product,
 *    table, hotel, umb, …) are kept verbatim because the Platform web app does not render
 *    them — they are unreachable here and exist only so the copied tree compiles as one
 *    unit. The product apps keep their own copy of this file.
 *
 * The few Platform-owned calls whose *method*, *path shape* or *body* cannot be expressed
 * as a constant are translated centrally in `main/initializers/platformApiAdapter.ts`,
 * which is installed on the shared axios instance. Keeping the translation in one place
 * is why the copied screens stay byte-identical to PoseidonFrontend.
 *
 * ---
 * (Historical note, kept because it documents a real defect in the source project:)
 * `import Expenses from "../../pages/expenses/Expenses";` used to sit at the top of this
 * file. It is an accidental leftover in PoseidonFrontend: a page component pulled into a
 * constants module that never references it, which also closes a circular import
 * (`ApiConstants` -> `Expenses` -> `ApiConstants`). Removing it changes no constant.
 */

export const ApiConstants = {
  /** PLATFORM — sign-in is owned by `features/auth/LoginPage`; kept for source parity. */
  Login: {
    login: 'auth/login',
  },
  umb: {
    list: '/api/umb/list',
    listct: '/api/umb/listct',
    umbInvnetory: 'api/umb/umbInventory',
    closeInvoice: 'api/umb/closeInventoryInvoice',
    update: '/api/umb/update',
    approve: '/api/umb/approve',
    setCader: '/api/umb/update',
    delete: '/api/umb/delete',
    umb: '/api/umb',
    switch: 'api/umb/switch',
  },
  umbReservation: {
    availability: '/api/umb-reservation/availability',
    create: '/api/umb-reservation/create',
    update: '/api/umb-reservation/update',
    delete: '/api/umb-reservation/delete',
    listCt: '/api/umb-reservation/listct',
    byId: (id: number) => `/api/umb-reservation/getreservation?id=${id}`,
    checkIn: '/api/umb-reservation/check-in',
    cancel: '/api/umb-reservation/cancel',
    statusForDate: '/api/umb-reservation/status-for-date',
  },
  hotel: {
    hotels: '/api/hotel',
    dashboard: '/api/hotel/dashboard',
    hotelListCt: '/api/hotel/listct',
    hotelSave: '/api/hotel/save',
    hotelDelete: '/api/hotel/delete',
    hotelImageUpload: '/api/hotel/image',
    packages: '/api/hotel/packages',
    packageSave: '/api/hotel/packages/save',
    packageDelete: '/api/hotel/packages/delete',

    roomTypes: '/api/hotel/roomtypes',
    roomTypesListCt: '/api/hotel/roomtypes/listct',
    roomTypeSave: '/api/hotel/roomtypes/save',
    roomTypeDelete: '/api/hotel/roomtypes/delete',
    onlineRoomType: `/api/hotel/roomtypesbyhotel`,

    rooms: '/api/hotel/hotelmanagement',
    roomListCt: '/api/hotel/rooms/listct',
    roomSave: '/api/hotel/rooms/save',
    roomDelete: '/api/hotel/rooms/delete',
    availability: '/api/hotel/availability',
    reservations: '/api/hotel/rezervations',
  },
  publicHotel: {
    list: '/public/hotel/list',
    bySlug: '/api/public/hotel/by-slug',
    roomTypes: '/api/public/hotel/roomtypes',
    availability: '/api/public/hotel/availability',
    bookingRequest: '/api/public/hotel/booking-request',
    bookingRequests: '/api/public/hotel/booking-requests',
  },
  publicUser: {
    register: '/api/public/user/register',
    login: '/api/public/user/login',
    monitor: '/api/public/user/monitor',
    cancelBookingRequest: '/api/public/user/booking-request/cancel',
    forgotPassword: '/api/public/user/forgot-password',
    resetPassword: '/api/public/user/reset-password',
    profile: '/api/public/user/profile',
    changePassword: '/api/public/user/change-password',
    verifyActivation: '/api/public/user/verify-activation',
    resendActivation: '/api/public/user/resend-activation',
  },
  hotelPricing: {
    hotels: 'api/hotel',
    roomTypes: 'api/hotel/roomtypes',

    seasonPeriodList: '/api/hotel/season-period/listct',
    seasonPeriodSave: '/api/hotel/season-period',
    seasonPeriodDelete: '/api/hotel/season-period/delete',

    ratePlanList: '/api/hotel/rate-plan/listct',
    ratePlanSave: '/api/hotel/rate-plan',
    ratePlanDelete: '/api/hotel/rate-plan/delete',

    roomRateList: '/api/hotel/room-rate/listct',
    roomRateSave: '/api/hotel/room-rate',
    roomRateDelete: '/api/hotel/room-rate/delete',
  },
  reservation: {
    create: '/api/reservation/create',
    availability: '/api/reservation/availability',
    publicAvailability: '/api/reservation/public/availability',
    checkIn: `/api/reservation/checkin`,
    checkOut: `/api/reservation/checkout`,
    cancel: `/api/reservation/cancel`,
    listCt: '/api/reservation/listct',
    addPackage: '/api/reservation/package/add',
    listPackages: '/api/reservation/package/list',
    deletePackage: '/api/reservation/package/delete',
    updatePackage: '/api/reservation/package/update',
  },
  hotelFolio: {
    create: '/api/hotel-folio/create',
    byReservation: '/api/hotel-folio/by-reservation',
    rows: '/api/hotel-folio/rows',
    payments: '/api/hotel-folio/payments',
    paymentsByReservation: '/api/hotel-folio/payments/by-reservation',
    paymentHistory: '/api/hotel-folio/paymenthistory',
    saveRow: '/api/hotel-folio/rows/save',
    deleteRow: '/api/hotel-folio/rows',
    payment: '/api/hotel-folio/payment',
    balance: '/api/hotel-folio/balance',
    close: '/api/hotel-folio/close',
    closeOpenedByUser: '/api/hotel-folio/closeopenedbyuser',
    printInvoice: '/api/hotel-folio/print-invoice',
    printDailySummary: '/api/hotel-folio/print-daily-summary',
  },
  bookingRequest: {
    bookingRequestListCt: '/api/bookingrequest/listct',
    bookingRequestCreate: '/api/bookingrequest/create',
    bookingRequestUpdateStatus: '/api/bookingrequest/updatestatus',
    bookingRequestDelete: '/api/bookingrequest/delete',
    bookingRequestProfileList: '/api/bookingrequest/profile/list',
    bookingRequestCreateProfile: '/api/bookingrequest/profile/create',
    bookingRequestCreatePublic: '/api/bookingrequest/public/create',
  },
  bookingChannel: {
    overview: '/api/booking-channel',
    addAccount: '/api/booking-channel/account',
    updateAccount: (id: number) => `/api/booking-channel/account?id=${id}`,
    addPropertyMap: '/api/booking-channel/property-map',
  },
  /*
   * PLATFORM (ADR-004: the Platform owns identity).
   *
   * The monolith had a `Company.User` table: one row per person *per tenant*. Architecture
   * 5.0 splits that into one global `identity.Users` row plus an `organization.Memberships`
   * row per organization, so every "user" route below is served by PlatformAPI's membership
   * surface and a grid row's `id` is a **membership** id.
   */
  User: {
    users: 'organization/memberships/users',
    listCt: 'organization/memberships/listct',
    // Upsert. Legacy sends PascalCase with the id in the body (`Id: 0` means create) and
    // reads back the new id, which is why this is not the REST `PUT .../{id}` route.
    update: 'organization/memberships/update',
    delete: 'organization/memberships/delete',
    changePassword: 'organization/memberships/change-password',
    // Fiscal printer authorization is a *product* concern (only RestaurantBE has fiscal
    // printers), so the Platform has no counterpart. Nothing in the ported tree calls it.
    toggleCanPrintFiscal: 'organization/memberships/toggle-can-print-fiscal',
    sendActivationOtp: 'organization/memberships/send-activation-otp',
    activate: 'organization/memberships/activate',
  },
  /** PLATFORM (ADR-004: the Platform owns roles and grants). */
  Roles: {
    list: 'authorization/roles',
    update: 'authorization/role/update',
    delete: 'authorization/role/delete',
    grants: 'authorization/role/grants',
    saveGrants: 'authorization/role/grants',
    transfer: 'authorization/role/transfer',
  },
  /**
   * PLATFORM — an organization is the successor of a Company, but a Platform session is
   * scoped to exactly one organization, so `list` returns the caller's own organization
   * only. The ported screens used this list for a cross-company picker; on Platform that
   * picker collapses to a single option (see the adapter's note on role `companyId`).
   */
  Company: {
    list: 'organizations',
    update: 'organization',
    provision: 'organization/provision',
    delete: 'organization/delete',
  },
  /**
   * PLATFORM — per-organization mail/notification targeting. The product owner's rule is
   * that each product decides *what* it needs to send, while the Platform owns *to whom*
   * and *through which channel* it may be sent, so this is org-scoped configuration.
   */
  companyEmailNotification: {
    list: 'organization/email-notification/list',
    save: 'organization/email-notification/save',
  },
  companyBalance: {
    report: '/api/company-balance/report',
  },

  expenses: {
    categories: {
      list: '/api/expense/category/list',
      listct: '/api/expense/category/listct',
      save: '/api/expense/category/save',
      delete: (id: number) => `/api/expense/category/delete?id=${id}`,
    },
    invoices: {
      list: '/api/expense/invoice/list',
      listct: '/api/expense/invoice/listct',
      byId: (id: number) => `/api/expense/invoice?id=${id}`,
      save: '/api/expense/invoice/save',
      delete: (id: number) => `/api/expense/invoice/delete?id=${id}`,
      summary: '/api/expense/invoice/summary',
    },
  },
  email: {
    send: '/api/email/send',
  },
  dailyInventoryClose: {
    previewUser: '/api/daily-inventory-close/preview-user',
    save: '/api/daily-inventory-close/save',
    byId: (id: number) => `/api/daily-inventory-close?id=${id}`,
    listCt: '/api/daily-inventory-close/listct',
    delete: (id: number) => `/api/daily-inventory-close/delete?id=${id}`,

  },
  dailyInventory: {
    list: '/api/daily-inventory/list',
    current: '/api/daily-inventory/current',
    byId: (id: number) => `/api/daily-inventory?id=${id}`,
    listCt: '/api/daily-inventory/listct',
    update: '/api/daily-inventory/update',
    delete: (id: number) => `/api/daily-inventory/delete?id=${id}`,
  },
  Currencies: {
    list: 'currencies/list',
    load: 'currencies/load',
    update: 'currencies/update',
  },
  integrations: {
    list: 'integrations',
    add: 'integrations',
  },
  integrationSettings: {
    companyOptions: 'integration-settings/company-options',
    byIntegrationId: (integrationId: number) => `integration-settings/list?integrationId=${integrationId}`,
    add: 'integration-settings/add',
    edit: 'integration-settings/edit',
    upsert: 'integration-settings/upsert',
    saveAll: 'integration-settings/save-all',
  },

  area: {
    list: '/api/area/list',
    listct: '/api/area/listct',
    update: '/api/area/update',
    delete: '/api/area/delete',
  },
  playground: {
    list: '/api/playground/list',
    listct: '/api/playground/listct',
    update: '/api/playground/update',
    delete: '/api/playground/delete',
    inventory: '/api/playground/inventory',
    closeInvoice: '/api/playground/closeInventoryInvoice',
    activeSessions: (playgroundId: number) => `/api/playground/sessions/active?playgroundId=${playgroundId}`,
    startSession: '/api/playground/sessions/start',
    endSession: '/api/playground/sessions/end',
  },
  row: {
    list: '/api/row/list',
    listct: '/api/row/listct',
    update: '/api/row/Update',
    delete: '/api/row/delete',
  },
  invoiceTemp: {
    list: 'api/invoice/getListInvoiceTempByStatus',
    updateInvoicesTemp: 'api/invoice/updateInvoicesTemp',
    invoiceByTable: 'api/invoice/bytable',
    closeInvoice: 'api/invoice/close',
  },
  invoice: {
    list: 'api/invoice/list',
    listCt: 'api/invoice/listCt',
    subjects: '/api/invoice/subjects',
    openedbyuser: 'api/invoice/opened-by-user',
    closeAllOpenedByUser: 'api/invoice/close-opened-by-user',
    delete: '/api/invoice/delete',
    update: 'api/invoice/update',
    rows: 'api/invoice/rows',
    history: 'api/invoice/history',
    ownerIssues: 'api/invoice/owner-issues',
    employeeList: '/api/invoice/employee/list',
    close: '/api/invoice/close',
    refiscalize: '/api/invoice/refiscalize',
    downloadPdf: '/api/invoice/download',
    filterByUser: '/api/invoice/filter-by-user',
    myUnpaid: '/api/invoice/user-invoice',
    updateAdminInvoice: 'api/invoice/updateadmininvoice',
    deleteRow: '/api/invoice/row',
  },

  Dashboard: {
    xxx: 'xxx',
    cards: 'api/utility/cards',
    graphs: 'api/utility/graphs',
    lastDayOrder: 'api/utility/lastDayOrder',
    cashBalanceList: 'api/utility/cash-balance/list',
    registerCashBalance: 'api/utility/cash-balance/register',
    serverHealth: 'api/server-health',
  },
  Products: {
    productCategory: 'api/product/productCategory',
    prodoductCategoryDelete: `api/product/productCategory/delete`,
    update: 'api/product/update',
    product: 'api/product',
    delete: 'api/product/delete',
    list: 'api/product/list',
    listct: 'api/product/listct',
  },
  productConfiguration: {
    byProduct: 'api/productConfiguration/byProduct',
    save: 'api/productConfiguration/save',
    disable: 'api/productConfiguration/disable',
  },
  /*
   * PLATFORM — the feed is the organization's internal landing timeline (ADR-004), served
   * by PlatformAPI at `/feed/*`.
   *
   * Nothing in the ported tree reads this group. `pages-old/mainLanding` does its feed work
   * through `Landing/platformFeedApi.ts`, which is the single place that knows the Platform
   * feed contract (cursor paging, GUID ids, `{content, visibility}` creates) and the single
   * place that records which sections have no endpoint behind them. The group is kept
   * retargeted rather than deleted because `ApiConstants` is the copied tree's route
   * registry and a future ported screen should reach for a correct entry here rather than
   * invent a second table — which is exactly the mistake `FEED_ENDPOINTS` was.
   *
   * Note the two typos carried over from PoseidonFrontend: `deletePosts` pointed at
   * `/api/feed/coments/{id}` and `deleteComents` at `/api/feed/posts/{id}` — the paths were
   * swapped. The corrected form below is what the Platform serves, and it is the shape
   * `platformFeedApi` uses.
   */
  feed: {
    posts: 'feed/posts',
    createPost: 'feed/posts',
    createComment: (postId: number | string) => `feed/posts/${postId}/comments`,
    deletePost: (postId: number | string) => `feed/posts/${postId}`,
    like: (postId: number | string) => `feed/posts/${postId}/likes`,
  },
  tables: {
    tables: '/tavolinat',
    updateTable: '/updateTavolin',
    list: '/api/table/list',
    update: '/api/table/update',
    listCt: '/api/table/listCT',
    delete: '/api/table/delete',
    printInvoice: '/api/invoice/printInvoice',
  },
  productMenuDetails: {
    adminList: '/api/productMenuDetails/admin/list',
    byProduct: '/api/productMenuDetails/byProduct',
    upsert: '/api/productMenuDetails/upsert',
    delete: '/api/productMenuDetails/delete',
    menu: '/api/productMenuDetails/public/menu',
    menuByKey: '/api/productMenuDetails/public/menu',
  },
  order: {
    list: '/api/product/order/list',
    listct: '/api/product/order/listct',
    update: '/api/product/order',
    delete: '/api/product/delete',
    complete: '/api/product/order/complete',
  },
  stock: {
    list: '/api/product/stock/list',
    listct: '/api/product/stock/listct',
    update: '/api/product/stock',
    delete: '/api/product/delete',
  },
  print: {
    printerApp: '/api/print/printer-app',
  },
  /*
   * PLATFORM — media is a **known gap**. PlatformAPI has no upload/download service yet, so
   * the routes below do not exist. They are retargeted anyway, from the monolith's
   * `/api/upload/*` onto one reserved `media/*` namespace, so that every reference in the
   * ported tree points at the same place and the eventual service has a single agreed prefix
   * rather than a scattering of legacy URLs. The adapter also rewrites any hard-coded
   * `/api/upload/...` literal onto it (see `platformApiAdapter.ts`), and silences the failure
   * toast so the gap does not surface as an error on every page.
   *
   * What works today: nothing here. Avatars, organization logos and post images do not
   * display or upload. `identity.Users.AvatarObjectKey` is the field the service is expected
   * to fill. Tracked as gap 1 in the ownership doc, section 6.5.
   */
  file: {
    upload: 'media/upload',
    download: 'media/download',
    downloadById: 'media/download/by-id',
    downloadByIdUrl: (id: number) => `media/download/by-id?id=${id}`,
    deleteById: (id: number) => `media/delete/by-id?id=${id}`,
    downloadUrl: (entityId: number, entityType: number, fileName?: string) =>
      `media/download?entityId=${entityId}&entityType=${entityType}${fileName ? `&fileName=${encodeURIComponent(fileName)}` : ''}`,
    filesByEntity: (entityId: number, entityType: number) => `media/files/by-entity?entityId=${entityId}&entityType=${entityType}`,
    metadataByEntity: 'media/metadata/by-entity',
    metadataByEntityIds: 'media/metadata/by-entity-ids',
  },

  /*
   * PLATFORM — `uploadCT` (grid column customization) and `getResource`/`saveResource`
   * (authorization resource metadata) are Platform-owned after the port.
   *
   * `uploadCT` is a **known gap**: PlatformAPI has no per-user grid-customization table, so
   * the route does not exist. `GeneralTable` already treats the call as fire-and-forget
   * (`.catch(() => {})`); the adapter additionally marks it `skipErrorToast` so the gap
   * does not turn into an error toast on every filter keystroke. Column layout therefore
   * resets on reload. Tracked in the ownership doc rather than faked with a 204, which
   * would tell the operator their layout had been saved.
   *
   * `getResource`/`saveResource` map onto the authorization catalog: the adapter turns the
   * legacy query-param form into `authorization/resources/{id}` and the POST into a PUT.
   */
  utility: {
    uploadCT: 'utility/columns',
    getResource: 'authorization/resources',
    saveResource: 'authorization/resources/save',

    // PRODUCT-owned (printing belongs to each product — see WP-003). PlatformFE renders no
    // printer screen, so these are unreachable here and kept only for tree parity.
    printers: 'utility/printers',
    printersCreate: 'utility/printers',
    printersUpdate: 'utility/printers',
    printersDelete: 'utility/printers',
  },
  logger: {
    listCt: 'api/logger/listct',
    byId: (id: number) => `api/logger?id=${id}`,
  },
  /*
   * PLATFORM (ADR-004: the Platform owns notification and activity).
   *
   * Notification *configuration* lives here too, at the product owner's direction: the
   * Platform owns the categories an organization may notify about and which user is
   * subscribed to which category, while each product decides which of those it actually
   * emits. Consumed by the ported `pages-old/notifications/*` screens.
   */
  notifications: {
    listCt: 'notifications/listct',
    count: 'notifications/count',
    // Legacy POSTs `.../read?id=`; PlatformAPI exposes `PUT /notifications/{id}/read`.
    // The adapter rewrites both the verb and the id-in-path form.
    read: (id: number | string) => `notifications/${id}/read`,
    readAll: 'notifications/read-all',
    categories: {
      list: 'notifications/categories',
      listCt: 'notifications/categories/listct',
      save: 'notifications/categories/save',
      delete: (id: number | string) => `notifications/categories/${id}`,
    },
    userCategories: {
      list: 'notifications/user-categories',
      listCt: 'notifications/user-categories/listct',
      save: 'notifications/user-categories/save',
      delete: (id: number | string) => `notifications/user-categories/${id}`,
    },
  },
  enum: {
    /*
     * PLATFORM — the legacy option dictionaries, served by PlatformAPI's
     * `Endpoints/UtilityEndpoints.cs`.
     *
     * The monolith kept one flat `Enum` table; Architecture 5.0 has no such table, so the
     * endpoint is a small hand-written dictionary instead: an option list is a constant
     * vocabulary, not tenant data, and entries are added when a ported screen needs one.
     * `NotificationCategoryCode` is there because `pages-old/notifications/NotificationCategoriesGrid`
     * builds its category-type dropdown from it — without the endpoint the request 404s and the
     * dropdown renders empty.
     *
     * The shape is the legacy one (`{ EnumName: [{ id, name }] }`); see the endpoint's doc
     * comment for why it was not modernised.
     */
    allEnums: 'utility/all-enums',
  },
  EntityTypes: {
    Product: 1,
    User: 2,
    Row: 3,
    Table: 4,
    Hotel: 5,
    Company: 6,
    Post: 7,
    Category: 8,
    Tag: 9,
    Subject: 10,
    Invoice: 11,
    InvoiceRow: 12,
    ProductCategory: 13,
    ProductStock: 14,
    Order: 15,
    Room: 16,
    RoomType: 17,
    Reservation: 18,
    Guest: 19,
    BookingRequest: 20,
    Notification: 21,
    UMB: 22,
    Area: 23,
    DailyInventoryClose: 24,
    DailyInventory: 25,
    Expenses: 26,
    ExpenseCategory: 27,
    Playground: 28,
    PlaygroundSession: 29,
  },
};
